import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Form, Modal } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { listEvents, submitVolunteerInterest } from "../services/api";

const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const buildMapsHref = (address = "") => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(String(address || "").trim())}`;
const initialEventVolunteerForm = {
  name: "",
  email: "",
  phone: "",
  message: "",
};

const getStartDateKey = (value) => {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const eventOccursOnDateKey = (event, dateKey) => {
  const startKey = getStartDateKey(event?.startDate);
  if (!startKey) return false;
  if (!event?.isMultiDay || !event?.endDate) {
    return startKey === dateKey;
  }
  const endKey = getStartDateKey(event.endDate);
  if (!endKey) return startKey === dateKey;
  return dateKey >= startKey && dateKey <= endKey;
};

const getEventEndTimestamp = (event) => {
  const endCandidate = event?.isMultiDay && event?.endDate ? event.endDate : event?.startDate;
  const stamp = new Date(endCandidate || "").getTime();
  return Number.isNaN(stamp) ? 0 : stamp;
};

const formatEventDateRange = (event) => {
  const start = new Date(event?.startDate || "");
  if (Number.isNaN(start.getTime())) return "Date not set";
  if (event?.isMultiDay && event?.endDate) {
    const end = new Date(event.endDate);
    if (!Number.isNaN(end.getTime())) {
      return `${start.toLocaleString()} - ${end.toLocaleString()}`;
    }
  }
  return start.toLocaleString();
};

const buildCalendarDays = (events, monthYear) => {
  if (!monthYear) return [];
  const [year, month] = monthYear.split("-").map(Number);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  const firstDayOffset = start.getDay();
  const days = [];

  for (let i = 0; i < firstDayOffset; i += 1) days.push(null);

  for (let day = 1; day <= end.getDate(); day += 1) {
    const date = new Date(year, month - 1, day);
    const dateKey = date.toISOString().slice(0, 10);
    const dayEvents = events.filter((event) => eventOccursOnDateKey(event, dateKey));
    days.push({ day, dateKey, events: dayEvents });
  }

  return days;
};

const getVolunteerNeedSummary = (neededVolunteers, assignedCount) => {
  const needed = Number(neededVolunteers || 0);
  const assigned = Number(assignedCount || 0);
  if (needed <= 0) {
    return null;
  }
  if (assigned > needed) {
    return { text: `Overbooked by ${assigned - needed}`, bg: "danger" };
  }
  if (assigned === needed) {
    return { text: "Fully staffed", bg: "success" };
  }
  return { text: `${needed - assigned} more volunteer${needed - assigned === 1 ? "" : "s"} needed`, bg: "warning" };
};

function EventImageStrip({ images = [], title }) {
  const safeImages = Array.isArray(images) ? images.filter(Boolean) : [];
  const firstImage = safeImages[0] || "";
  const [activeImage, setActiveImage] = useState(firstImage);

  useEffect(() => {
    setActiveImage(firstImage);
  }, [firstImage]);

  if (!safeImages.length) return null;

  return (
    <div className="d-flex flex-column gap-2 mb-3">
      <img
        src={activeImage}
        alt={title}
        className="event-gallery-main"
      />
      {safeImages.length > 1 && (
        <div className="d-flex gap-2 flex-wrap">
          {safeImages.map((url, index) => (
            <button
              type="button"
              key={`${title}-thumb-${index}`}
              className={`event-gallery-thumb-btn ${activeImage === url ? "is-active" : ""}`}
              onClick={() => setActiveImage(url)}
              aria-label={`Show ${title} media ${index + 1}`}
            >
              <img
                src={url}
                alt={`${title} ${index + 1}`}
                className="event-gallery-thumb"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EventVideoPreview({ videoUrl = "", title, onOpen }) {
  if (!videoUrl) return null;
  return (
    <div className="mb-3">
      <button type="button" className="event-video-preview-btn" onClick={() => onOpen(videoUrl, title)}>
        <video src={videoUrl} className="event-gallery-main" muted preload="metadata" />
        <span className="event-video-preview-label">Open video</span>
      </button>
      <video src={videoUrl} className="w-100 mt-2" controls muted playsInline preload="metadata" />
    </div>
  );
}

function EventsPage() {
  const { eventId } = useParams();
  const [events, setEvents] = useState([]);
  const [monthYear, setMonthYear] = useState(new Date().toISOString().slice(0, 7));
  const [selectedDateKey, setSelectedDateKey] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalEvents, setModalEvents] = useState([]);
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  const [selectedEventSignup, setSelectedEventSignup] = useState(null);
  const [showEventVolunteerModal, setShowEventVolunteerModal] = useState(false);
  const [eventVolunteerForm, setEventVolunteerForm] = useState(initialEventVolunteerForm);
  const [eventVolunteerStatus, setEventVolunteerStatus] = useState({ type: "", message: "" });
  const [showVolunteerThanksModal, setShowVolunteerThanksModal] = useState(false);
  const [thanksMessage, setThanksMessage] = useState("");
  const [copiedEventId, setCopiedEventId] = useState(null);
  const [copyStatus, setCopyStatus] = useState({ type: "", message: "" });
  const [videoViewer, setVideoViewer] = useState({ show: false, url: "", title: "" });

  const copyEventLink = async (id) => {
    const link = `${window.location.origin}/events/${id}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const input = document.createElement("textarea");
        input.value = link;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setCopiedEventId(String(id));
      setCopyStatus({ type: "success", message: `${link} copied to the clipboard.` });
      window.setTimeout(() => setCopiedEventId(null), 1500);
      window.setTimeout(() => setCopyStatus({ type: "", message: "" }), 2500);
    } catch {
      setCopyStatus({ type: "danger", message: "Unable to copy automatically. Use the prompt to copy the link." });
      window.prompt("Copy this event link:", link);
    }
  };

  const openVideoViewer = (url, title) => setVideoViewer({ show: true, url, title: title || "Event video" });

  const openVolunteerDetails = (eventTitle, assignment) => {
    setSelectedVolunteer({
      eventTitle,
      name: assignment?.publicName || assignment?.fullName || "Volunteer",
      assignedTime: assignment?.assignedTime || null,
      role: assignment?.role || null,
      bringItems: assignment?.bringItems || null,
    });
  };

  const openEventVolunteerSignup = (event) => {
    setSelectedEventSignup(event);
    setEventVolunteerStatus({ type: "", message: "" });
    setEventVolunteerForm(initialEventVolunteerForm);
    setShowEventVolunteerModal(true);
  };

  const handleEventVolunteerChange = (event) => {
    const { name, value } = event.target;
    setEventVolunteerForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEventVolunteerSubmit = (event) => {
    event.preventDefault();
    if (!selectedEventSignup?.id) return;
    setEventVolunteerStatus({ type: "", message: "" });
    submitVolunteerInterest({
      ...eventVolunteerForm,
      interests: "",
      availability: "",
      eventInterestType: "specific",
      preferredEventId: selectedEventSignup.id,
    })
      .then((res) => {
        setEventVolunteerStatus({ type: "success", message: res.mockMode ? "Saved locally (mock mode)." : "Volunteer interest submitted." });
        setShowEventVolunteerModal(false);
        setThanksMessage(`Thank you for your interest in ${selectedEventSignup.title}. We will review your submission and follow up soon.`);
        setShowVolunteerThanksModal(true);
      })
      .catch(() => setEventVolunteerStatus({ type: "danger", message: "Failed to submit volunteer interest." }))
      .finally(() => setEventVolunteerForm(initialEventVolunteerForm));
  };

  useEffect(() => {
    const loadEvents = async () => {
      const response = await listEvents();
      setEvents(response.data || []);
    };
    loadEvents();
  }, []);

  const publicEvents = useMemo(() => events.filter((event) => event.isPublic).sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()), [events]);
  const now = Date.now();
  const upcomingEvents = useMemo(() => publicEvents.filter((event) => getEventEndTimestamp(event) >= now), [publicEvents, now]);
  const topThreeUpcoming = useMemo(() => upcomingEvents.slice(0, 3), [upcomingEvents]);
  const pastEvents = useMemo(() => publicEvents.filter((event) => getEventEndTimestamp(event) < now).reverse(), [publicEvents, now]);
  const calendarDays = useMemo(() => buildCalendarDays(publicEvents, monthYear), [monthYear, publicEvents]);
  const linkedEvent = useMemo(() => publicEvents.find((event) => String(event.id) === String(eventId)), [publicEvents, eventId]);

  const openDayModal = (dateKey, dayEvents) => {
    if (!dayEvents.length) return;
    setSelectedDateKey(dateKey);
    setModalEvents(dayEvents);
    setIsModalOpen(true);
  };

  return (
    <section>
      {copyStatus.message && (
        <div className={`copy-feedback-toast ${copyStatus.type === "danger" ? "is-error" : "is-success"}`}>
          {copyStatus.message}
        </div>
      )}

      {eventId && linkedEvent && (
        <Card className="event-preview-card resource-card text-light mb-4 border-warning">
          <Card.Body>
            <h2 className="h5 mb-2">Linked Event</h2>
            <EventImageStrip
              images={linkedEvent.imageUrls?.length ? linkedEvent.imageUrls : linkedEvent.imageUrl ? [linkedEvent.imageUrl] : []}
              title={linkedEvent.title}
            />
            <EventVideoPreview videoUrl={linkedEvent.videoUrl} title={linkedEvent.title} onOpen={openVideoViewer} />
            <h3 className="h4 mb-1">{linkedEvent.title}</h3>
            <Button
              size="sm"
              variant="outline-info"
              className="mb-2"
              onClick={() => copyEventLink(linkedEvent.id)}
            >
              {copiedEventId === String(linkedEvent.id) ? "Copied" : "Copy Event Link"}
            </Button>
            <p className="small text-muted-soft mb-2">{formatEventDateRange(linkedEvent)}</p>
            <p className="small text-muted-soft mb-2">{linkedEvent.details || "No details provided yet."}</p>
            {linkedEvent.eventAddress && (
              <p className="small text-muted-soft mb-2">
                {linkedEvent.eventAddress}{" "}
                <a href={buildMapsHref(linkedEvent.eventAddress)} target="_blank" rel="noreferrer">
                  Map
                </a>
              </p>
            )}
            {(() => {
              const need = getVolunteerNeedSummary(linkedEvent.neededVolunteers, linkedEvent.assignedVolunteerCount || 0);
              if (!need) return null;
              return (
                <Button
                  size="sm"
                  variant={need.bg === "success" ? "outline-success" : need.bg === "danger" ? "outline-danger" : "outline-warning"}
                  className="mb-2"
                  onClick={() => openEventVolunteerSignup(linkedEvent)}
                >
                  {need.text}
                </Button>
              );
            })()}
            <div className="d-flex flex-wrap gap-2">
              {(linkedEvent.assignedVolunteers || []).length > 0 && (
                <span className="small text-muted-soft fw-semibold align-self-center">Volunteers</span>
              )}
              {(linkedEvent.assignedVolunteers || []).map((assignment, idx) => (
                <Button
                  key={`linked-volunteer-${linkedEvent.id}-${idx}`}
                  size="sm"
                  variant="outline-warning"
                  onClick={() => openVolunteerDetails(linkedEvent.title, assignment)}
                >
                  {assignment.publicName || "Volunteer"}
                </Button>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      <Card className="glass-card text-light mb-4">
        <Card.Body>
          <h1 className="section-title mb-1">Events</h1>
          <p className="text-muted-soft mb-3">Upcoming opportunities, calendar planning, and past event archive.</p>

          {topThreeUpcoming.length === 0 ? (
            <p className="text-muted-soft mb-0">No upcoming events are scheduled.</p>
          ) : (
            <div className="d-flex flex-column gap-3">
              {topThreeUpcoming.map((event) => (
                <Card key={`top-${event.id}`} className="event-preview-card resource-card text-light">
                  <Card.Body className="event-preview-body">
                    <EventImageStrip
                      images={event.imageUrls?.length ? event.imageUrls : event.imageUrl ? [event.imageUrl] : []}
                      title={event.title}
                    />
                    <EventVideoPreview videoUrl={event.videoUrl} title={event.title} onOpen={openVideoViewer} />
                    <h3 className="h4 mb-1">{event.title}</h3>
                    <Button
                      size="sm"
                      variant="outline-info"
                      className="mb-2"
                      onClick={() => copyEventLink(event.id)}
                    >
                      {copiedEventId === String(event.id) ? "Copied" : "Copy Event Link"}
                    </Button>
                    <p className="small text-muted-soft mb-2">{formatEventDateRange(event)}</p>
                    <p className="small text-muted-soft mb-2">{event.details || "No details provided yet."}</p>
                    {event.eventAddress && (
                      <p className="small text-muted-soft mb-2">
                        {event.eventAddress}{" "}
                        <a href={buildMapsHref(event.eventAddress)} target="_blank" rel="noreferrer">
                          Map
                        </a>
                      </p>
                    )}
                    {(() => {
                      const need = getVolunteerNeedSummary(event.neededVolunteers, event.assignedVolunteerCount || 0);
                      if (!need) return null;
                      return (
                        <Button
                          size="sm"
                          variant={need.bg === "success" ? "outline-success" : need.bg === "danger" ? "outline-danger" : "outline-warning"}
                          className="mb-2"
                          onClick={() => openEventVolunteerSignup(event)}
                        >
                          {need.text}
                        </Button>
                      );
                    })()}
                    <div className="d-flex flex-wrap gap-2">
                      {(event.assignedVolunteers || []).length > 0 && (
                        <span className="small text-muted-soft fw-semibold align-self-center">Volunteers</span>
                      )}
                      {(event.assignedVolunteers || []).map((assignment, idx) => (
                        <Button
                          key={`${event.id}-volunteer-${idx}`}
                          size="sm"
                          variant="outline-warning"
                          onClick={() => openVolunteerDetails(event.title, assignment)}
                        >
                          {assignment.publicName || "Volunteer"}
                        </Button>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="glass-card text-light mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="h4 mb-0">Calendar</h2>
            <Form.Control
              type="month"
              style={{ maxWidth: "190px" }}
              value={monthYear}
              onChange={(e) => setMonthYear(e.target.value)}
              size="sm"
            />
          </div>

          <div className="calendar-grid mb-2 text-muted-soft">
            {weekdayLabels.map((label) => (
              <div
                key={label}
                className="text-center fw-semibold small"
              >
                {label}
              </div>
            ))}
          </div>

          <div className="calendar-grid calendar-grid-lg">
            {calendarDays.map((day, index) =>
              day ? (
                <button
                  type="button"
                  key={`${day.dateKey}-${index}`}
                  className={`calendar-day text-start ${day.events.length ? "has-event" : ""}`}
                  onClick={() => openDayModal(day.dateKey, day.events)}
                >
                  <div className="small fw-semibold">{day.day}</div>
                  {day.events.slice(0, 2).map((eventItem) => (
                    <div
                      key={`calendar-${eventItem.id}`}
                      className="small text-muted-soft text-truncate"
                    >
                      {eventItem.title}
                    </div>
                  ))}
                </button>
              ) : (
                <div
                  key={`empty-${index}`}
                  className="calendar-day opacity-50"
                />
              ),
            )}
          </div>
        </Card.Body>
      </Card>

      <Card className="glass-card text-light">
        <Card.Body>
          <h2 className="h4 mb-3">Past Events</h2>
          {pastEvents.length === 0 ? (
            <p className="text-muted-soft mb-0">No past events to display.</p>
          ) : (
            <div className="d-flex flex-column gap-3">
              {pastEvents.map((event) => (
                <Card key={`past-${event.id}`} className="event-preview-card resource-card text-light">
                  <Card.Body className="event-preview-body">
                    <EventImageStrip
                      images={event.imageUrls?.length ? event.imageUrls : event.imageUrl ? [event.imageUrl] : []}
                      title={event.title}
                    />
                    <EventVideoPreview videoUrl={event.videoUrl} title={event.title} onOpen={openVideoViewer} />
                    <h3 className="h5 mb-1">{event.title}</h3>
                    <Button
                      size="sm"
                      variant="outline-info"
                      className="mb-2"
                      onClick={() => copyEventLink(event.id)}
                    >
                      {copiedEventId === String(event.id) ? "Copied" : "Copy Event Link"}
                    </Button>
                    <p className="small text-muted-soft mb-2">{formatEventDateRange(event)}</p>
                    <p className="small text-muted-soft mb-2">{event.details || "No details provided."}</p>
                    {event.eventAddress && (
                      <p className="small text-muted-soft mb-2">
                        {event.eventAddress}{" "}
                        <a href={buildMapsHref(event.eventAddress)} target="_blank" rel="noreferrer">
                          Map
                        </a>
                      </p>
                    )}
                    {(() => {
                      const need = getVolunteerNeedSummary(event.neededVolunteers, event.assignedVolunteerCount || 0);
                      if (!need) return null;
                      return (
                        <Button
                          size="sm"
                          variant={need.bg === "success" ? "outline-success" : need.bg === "danger" ? "outline-danger" : "outline-warning"}
                          className="mb-2"
                          onClick={() => openEventVolunteerSignup(event)}
                        >
                          {need.text}
                        </Button>
                      );
                    })()}
                    <div className="d-flex flex-wrap gap-2">
                      {(event.assignedVolunteers || []).length > 0 && (
                        <span className="small text-muted-soft fw-semibold align-self-center">Volunteers</span>
                      )}
                      {(event.assignedVolunteers || []).map((assignment, idx) => (
                        <Button
                          key={`past-volunteer-${event.id}-${idx}`}
                          size="sm"
                          variant="outline-warning"
                          onClick={() => openVolunteerDetails(event.title, assignment)}
                        >
                          {assignment.publicName || "Volunteer"}
                        </Button>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={isModalOpen}
        onHide={() => setIsModalOpen(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedDateKey ? new Date(`${selectedDateKey}T12:00:00`).toLocaleDateString() : "Event Details"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalEvents.map((event) => (
            <Card key={`modal-${event.id}`} className="mb-3 event-preview-card resource-card text-light">
              <Card.Body className="event-preview-body">
                <EventImageStrip
                  images={event.imageUrls?.length ? event.imageUrls : event.imageUrl ? [event.imageUrl] : []}
                  title={event.title}
                />
                <EventVideoPreview videoUrl={event.videoUrl} title={event.title} onOpen={openVideoViewer} />
                <h4 className="h6 mb-1">{event.title}</h4>
                <Button
                  size="sm"
                  variant="outline-info"
                  className="mb-2"
                  onClick={() => copyEventLink(event.id)}
                >
                  {copiedEventId === String(event.id) ? "Copied" : "Copy Event Link"}
                </Button>
                <p className="small text-muted-soft mb-2">{formatEventDateRange(event)}</p>
                <p className="small mb-2">{event.details || "No details provided."}</p>
                {event.eventAddress && (
                  <p className="small text-muted-soft mb-2">
                    {event.eventAddress}{" "}
                    <a href={buildMapsHref(event.eventAddress)} target="_blank" rel="noreferrer">
                      Map
                    </a>
                  </p>
                )}
                {(() => {
                  const need = getVolunteerNeedSummary(event.neededVolunteers, event.assignedVolunteerCount || 0);
                  if (!need) return null;
                  return (
                    <Button
                      size="sm"
                      variant={need.bg === "success" ? "outline-success" : need.bg === "danger" ? "outline-danger" : "outline-warning"}
                      className="mb-2"
                      onClick={() => openEventVolunteerSignup(event)}
                    >
                      {need.text}
                    </Button>
                  );
                })()}
                <div className="d-flex flex-wrap gap-2">
                  {(event.assignedVolunteers || []).length > 0 && (
                    <span className="small text-muted-soft fw-semibold align-self-center">Volunteers</span>
                  )}
                  {(event.assignedVolunteers || []).map((assignment, idx) => (
                    <Button
                      key={`modal-volunteer-${event.id}-${idx}`}
                      size="sm"
                      variant="outline-warning"
                      onClick={() => openVolunteerDetails(event.title, assignment)}
                    >
                      {assignment.publicName || "Volunteer"}
                    </Button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          ))}
          {modalEvents.length === 0 && <Alert variant="secondary">No events for this day.</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setIsModalOpen(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>


      <Modal
        show={videoViewer.show}
        onHide={() => setVideoViewer({ show: false, url: "", title: "" })}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{videoViewer.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {videoViewer.url && <video src={videoViewer.url} className="w-100" controls muted playsInline preload="metadata" />}
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(selectedVolunteer)}
        onHide={() => setSelectedVolunteer(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Volunteer Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedVolunteer && (
            <div className="small">
              <p className="mb-2">
                <strong>Event:</strong> {selectedVolunteer.eventTitle}
              </p>
              <p className="mb-2">
                <strong>Volunteer:</strong> {selectedVolunteer.name}
              </p>
              <p className="mb-2">
                <strong>Time:</strong> {selectedVolunteer.assignedTime || "TBD"}
              </p>
              <p className="mb-2">
                <strong>Job:</strong> {selectedVolunteer.role || "TBD"}
              </p>
              <p className="mb-0">
                <strong>What to bring:</strong> {selectedVolunteer.bringItems || "TBD"}
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setSelectedVolunteer(null)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEventVolunteerModal}
        onHide={() => setShowEventVolunteerModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Volunteer for This Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {eventVolunteerStatus.message && <Alert variant={eventVolunteerStatus.type}>{eventVolunteerStatus.message}</Alert>}
          {selectedEventSignup && (
            <p className="small text-muted-soft">
              <strong>{selectedEventSignup.title}</strong> | {formatEventDateRange(selectedEventSignup)}
            </p>
          )}
          <Form onSubmit={handleEventVolunteerSubmit}>
            <Form.Group className="mb-2">
              <Form.Label>Name</Form.Label>
              <Form.Control required name="name" value={eventVolunteerForm.name} onChange={handleEventVolunteerChange} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Email</Form.Label>
              <Form.Control required type="email" name="email" value={eventVolunteerForm.email} onChange={handleEventVolunteerChange} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Phone</Form.Label>
              <Form.Control name="phone" value={eventVolunteerForm.phone} onChange={handleEventVolunteerChange} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Message</Form.Label>
              <Form.Control as="textarea" rows={3} name="message" value={eventVolunteerForm.message} onChange={handleEventVolunteerChange} />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowEventVolunteerModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="cta-btn">
                Submit Event Interest
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showVolunteerThanksModal}
        onHide={() => setShowVolunteerThanksModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Thanks for Submitting</Modal.Title>
        </Modal.Header>
        <Modal.Body>{thanksMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVolunteerThanksModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}

export default EventsPage;
